import { useEffect, useState } from 'react';

import { authOptions } from 'api/auth';
// Consume the client generated by the shared APIs library.
import { MFAService, Policy } from 'api/shared/mfa/v1/api.pb';
import { useKeycloakContext } from 'containers/App/keycloak/context';
import { needsMFAReauthentication } from 'containers/UserProfile/mfaUtils';
import { useMFAStatus } from 'containers/UserProfile/useMFAStatus';

// Keep policy updates, error recovery and status refresh outside the settings component.
export function useMFAPolicy() {
  const { data, error, isPending, refetch } = useMFAStatus();
  const { reauthenticateForMFA } = useKeycloakContext();
  const [required, setRequired] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>();
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) setRequired(data.policy === Policy.REQUIRED);
  }, [data]);

  const save = async () => {
    setSaving(true);
    setSaveError(undefined);
    setSaved(false);
    try {
      const response = await MFAService.UpdatePolicy(
        { policy: required ? Policy.REQUIRED : Policy.OPTIONAL },
        {
          ...authOptions(),
          headers: {
            ...authOptions().headers,
            'Content-Type': 'application/json',
          },
        },
      );
      if (response.reauthentication_required) {
        await reauthenticateForMFA('settings');
      } else {
        const refreshed = await refetch();
        if (refreshed.isError) throw refreshed.error;
        setSaved(true);
      }
    } catch (err) {
      setSaveError(err);
      // A partial Keycloak update is reported by fresh status instead of a success message.
      await refetch();
    } finally {
      setSaving(false);
    }
  };

  const unchanged = !!data && required === (data.policy === Policy.REQUIRED);
  const needsRepair =
    data?.policy === Policy.REQUIRED && !data.enforcement_active;
  const needsReauthentication =
    needsMFAReauthentication(saveError) || needsMFAReauthentication(error);
  const reauthenticate = async () => {
    try {
      await reauthenticateForMFA('settings');
    } catch (err) {
      setSaveError(err);
    }
  };

  return {
    data,
    error,
    isPending,
    refetch,
    required,
    setRequired,
    saving,
    saveError,
    saved,
    setSaved,
    save,
    unchanged,
    needsRepair,
    needsReauthentication,
    reauthenticate,
  };
}
